import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';

import Container from '../../../../components/Container';
import AlertMsg from '../../../../components/AlertMsg';
import RestaurantHeader from '../../../../components/RestaurantHeader';
import AvailabilityRules from '../components/availability-rules';
import UploadFile from '../../../../components/UploadFile';

import { TCouponForm, couponFormSchema, ORDER_TYPE_LABELS, DEFAULT_CURRENCY, TMenuCategory } from '../schemas';
import { useGetCoupon, useCreateCoupon, useUpdateCoupon, useGetMenuCategories } from '../services';
import { CURRENCIES } from '../../../../constants';
import * as Api from '../../../../api';
import { useState } from 'react';

const defaultValues: TCouponForm = {
  code: '',
  description: '',
  status: 'active',
  discountType: 'percentage',
  currency: DEFAULT_CURRENCY,
  _idCategories: [],
  discountPercentage: null,
  discountAmount: { value: null, currency: DEFAULT_CURRENCY },
  maxDiscount: { value: null, currency: DEFAULT_CURRENCY },
  minPurchase: { value: null, currency: DEFAULT_CURRENCY },
  usageLimit: null,
  userUsageLimit: null,
  validFrom: null,
  validTo: null,
  applicableOrderType: [],
  tiers: [],
  availabilityRules: [],
};

const CouponFormPage = () => {
  const { rest_id: restaurantId, coupon_id: couponId } = useParams<{
    rest_id: string;
    coupon_id?: string;
  }>();
  const navigate = useNavigate();
  const isEdit = !!couponId;

  const [msgAlert, setMsgAlert] = useState({ open: false, message: '', msgType: 'error' });
  const [bannerImage, setBannerImage] = useState<string | null>(null);
  const [imageList, setImageList] = useState<any[]>([]);
  const [imageLoading, setImageLoading] = useState(false);

  const { data: couponData, isLoading: isLoadingCoupon, isError: isCouponError, error: couponError } = useGetCoupon(
    restaurantId || '',
    couponId || '',
    { enabled: isEdit }
  );

  const { mutate: createCoupon, isPending: isCreating } = useCreateCoupon(restaurantId || '');
  const { mutate: updateCoupon, isPending: isUpdating } = useUpdateCoupon(restaurantId || '');

  const { data: categoriesData = [], isLoading: isLoadingCategories, isError: isCategoriesError } = useGetMenuCategories(restaurantId || '');

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(couponFormSchema),
    defaultValues,
    mode: 'onChange',
  });

  const { fields: tierFields, append: appendTier, remove: removeTier } = useFieldArray({
    control,
    name: 'tiers',
  });

  const discountType = watch('discountType');

  const handleUploadFile = (data: any, file: File) => {
    setImageLoading(true);
    setImageList([data]);
    const formData = new FormData();
    formData.append('image', file);
    Api.uploadImageFile(formData).then((response: any) => {
      if (response.success) {
        const url: string = response.data.location;
        data.name = response.data.fileId;
        data.image = url;
        data.upload = true;
        setImageList([{ ...data }]);
        setBannerImage(url);
      } else {
        setImageList([]);
        setMsgAlert({ open: true, message: response.msg, msgType: 'error' });
      }
      setImageLoading(false);
    });
  };

  const handleRemoveImage = () => {
    setImageList([]);
    setBannerImage(null);
  };

  const handleUploadError = (err: string) => {
    setMsgAlert({ open: true, message: err, msgType: 'error' });
  };

  useEffect(() => {
    if (isEdit && couponData?.data) {
      const coupon = couponData.data;
      const currency = coupon.discountAmount?.currency || coupon.maxDiscount?.currency || coupon.minPurchase?.currency || DEFAULT_CURRENCY;

      if (coupon.bannerImage) {
        setBannerImage(coupon.bannerImage);
        setImageList([{
          id: new Date(),
          name: coupon.bannerImage,
          displayName: 'Banner Image',
          size: '0',
          image: coupon.bannerImage,
          upload: true,
        }]);
      } else {
        setBannerImage(null);
        setImageList([]);
      }

      reset({
        code: coupon.code,
        description: coupon.description || '',
        status: coupon.status || 'active',
        discountType: coupon.discountType,
        currency: currency,
        _idCategories: coupon._idCategories || [],
        discountPercentage: coupon.discountPercentage || null,
        discountAmount: coupon.discountAmount || { value: null, currency: DEFAULT_CURRENCY },
        maxDiscount: coupon.maxDiscount || { value: null, currency: DEFAULT_CURRENCY },
        minPurchase: coupon.minPurchase || { value: null, currency: DEFAULT_CURRENCY },
        usageLimit: coupon.usageLimit ?? null,
        userUsageLimit: coupon.userUsageLimit ?? null,
        validFrom: coupon.validFrom ? coupon.validFrom.slice(0, 10) : null,
        validTo: coupon.validTo ? coupon.validTo.slice(0, 10) : null,
        applicableOrderType: coupon.applicableOrderType || [],
        tiers: coupon.tiers || [],
        availabilityRules: coupon.availabilityRules || [],
      });
    } else if (!isEdit) {
      reset(defaultValues);
    }
  }, [isEdit, couponData, reset]);

  const onFormSubmit = (data: TCouponForm) => {
    const payload: TCouponForm = {
      ...data,
      code: data.code, // Already transformed to uppercase by Zod
      description: data.description || undefined,
      discountAmount: data.discountAmount?.value ? { value: data.discountAmount.value, currency: data.currency } : undefined,
      maxDiscount: data.maxDiscount?.value ? { value: data.maxDiscount.value, currency: data.currency } : undefined,
      minPurchase: data.minPurchase?.value ? { value: data.minPurchase.value, currency: data.currency } : undefined,
      bannerImage: bannerImage ?? null,
    };

    if (isEdit && couponId) {
      updateCoupon(
        { id: couponId, data: payload },
        {
          onSuccess: () => {
            setMsgAlert({ open: true, message: 'Coupon updated successfully', msgType: 'success' });
            setTimeout(() => navigate(`/restaurant/${restaurantId}/coupons`), 1500);
          },
          onError: (error: Error | { response?: { data?: { msg?: string } } }) => {
            const errorMsg = (error as any)?.response?.data?.msg || 'Failed to update coupon';
            setMsgAlert({
              open: true,
              message: errorMsg,
              msgType: 'error',
            });
          },
        }
      );
    } else {
      createCoupon(payload, {
        onSuccess: () => {
          setMsgAlert({ open: true, message: 'Coupon created successfully', msgType: 'success' });
          setTimeout(() => navigate(`/restaurant/${restaurantId}/coupons`), 1500);
        },
        onError: (error: Error | { response?: { data?: { msg?: string } } }) => {
          const errorMsg = (error as any)?.response?.data?.msg || 'Failed to create coupon';
          setMsgAlert({
            open: true,
            message: errorMsg,
            msgType: 'error',
          });
        },
      });
    }
  };

  const handleCancel = () => {
    navigate(`/restaurant/${restaurantId}/coupons`);
  };

  if (!restaurantId) {
    return (
      <Container page="restaurants" searchVal="" onSearch={() => null} searchLabel="">
        <Grid container item xs={12}>
          <Grid item xs={12} mb={2}>
            <Typography color="error">Restaurant ID is missing</Typography>
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (isEdit && isLoadingCoupon) {
    return (
      <Container page="restaurants" searchVal="" onSearch={() => null} searchLabel="">
        <Grid container item xs={12}>
          <Grid item xs={12} mb={2}>
            <RestaurantHeader
              title="Loading..."
              onBackBtnClick={() => navigate(`/restaurant/${restaurantId}/coupons`)}
            />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (isEdit && isCouponError) {
    const is404 = (couponError as any)?.response?.status === 404;
    const errorMsg = is404 
      ? 'Coupon not found. It may have been deleted.' 
      : (couponError as any)?.response?.data?.msg || 'Failed to load coupon details.';
    
    return (
      <Container page="restaurants" searchVal="" onSearch={() => null} searchLabel="">
        <Grid container item xs={12}>
          <Grid item xs={12} mb={2}>
            <RestaurantHeader
              title="Error"
              onBackBtnClick={() => navigate(`/restaurant/${restaurantId}/coupons`)}
            />
          </Grid>
          <Grid item xs={12} sx={styles.mainView} px={3} pt={3}>
            <Box sx={styles.errorContainer}>
              <Typography sx={styles.errorTitle}>
                {is404 ? 'Coupon Not Found' : 'Error Loading Coupon'}
              </Typography>
              <Typography sx={styles.errorMessage}>{errorMsg}</Typography>
              <Button 
                variant="contained" 
                onClick={() => navigate(`/restaurant/${restaurantId}/coupons`)}
                sx={styles.backButton}
              >
                Back to Coupons
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    );
  }

  return (
    <Container page="restaurants" searchVal="" onSearch={() => null} searchLabel="">
      <Grid container item xs={12}>
        <Grid item xs={12} mb={2}>
          <RestaurantHeader
            title={isEdit ? 'Edit Coupon' : 'Create New Coupon'}
            onBackBtnClick={() => navigate(`/restaurant/${restaurantId}/coupons`)}
          />
        </Grid>

        <Grid item xs={12} sx={styles.mainView} px={3} pt={3} pb={4}>
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <Grid container spacing={3}>
              {/* Banner Image */}
              <Grid item xs={12} sm={6}>
                <Typography sx={styles.sectionTitle}>Banner Image</Typography>
                <Typography sx={styles.sectionHint}>Optional image shown to customers with this coupon</Typography>
                <UploadFile
                  page="addResItemImg"
                  imageList={imageList}
                  imageLoading={imageLoading}
                  uploadFile={handleUploadFile}
                  uploadError={handleUploadError}
                  onRemoveImage={handleRemoveImage}
                  maxFiles={1}
                  uploadText="Drag & drop a banner image here"
                  uploadHeight="180px"
                />
              </Grid>

              {/* Basic Information */}
              <Grid item xs={12}>
                <Divider sx={styles.divider} />
                <Typography sx={styles.sectionTitle}>Basic Information</Typography>
              </Grid>

              {/* Code */}
              <Grid item xs={12} sm={6}>
                <Box sx={styles.fieldWrapper}>
                  <Controller
                    control={control}
                    name="code"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Coupon Code"
                        fullWidth
                        size="small"
                        required
                        error={!!errors.code}
                        helperText={errors.code?.message || `${field.value.length}/20 characters`}
                        onChange={(e) => field.onChange(e.target.value.replace(/[^A-Za-z0-9_-]/g, ''))}
                        InputProps={{
                          endAdornment: (
                            <Tooltip title="A unique code customers will enter to redeem this coupon. Must be 1-20 characters." arrow>
                              <InfoOutlinedIcon sx={styles.infoIcon} />
                            </Tooltip>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
              </Grid>

              {/* Description */}
              <Grid item xs={12} sm={6}>
                <Box sx={styles.fieldWrapper}>
                  <Controller
                    control={control}
                    name="description"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        fullWidth
                        size="small"
                        error={!!errors.description}
                        helperText={errors.description?.message}
                        InputProps={{
                          endAdornment: (
                            <Tooltip title="Optional description to help you remember what this coupon is for (not shown to customers)." arrow>
                              <InfoOutlinedIcon sx={styles.infoIcon} />
                            </Tooltip>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
              </Grid>

              {/* Status */}
              <Grid item xs={12} sm={6}>
                <Box sx={styles.fieldWrapper}>
                  <Controller
                    control={control}
                    name="status"
                    render={({ field }) => (
                      <FormControl fullWidth size="small" error={!!errors.status} required>
                        <InputLabel>Status</InputLabel>
                        <Select {...field} label="Status">
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="disabled">Disabled</MenuItem>
                        </Select>
                        {errors.status && (
                          <FormHelperText>{errors.status.message}</FormHelperText>
                        )}
                        <FormHelperText>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span>Active coupons can be used by customers. Disabled coupons are hidden.</span>
                            <Tooltip title="Only active coupons are visible and redeemable by customers." arrow>
                              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                            </Tooltip>
                          </Box>
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Box>
              </Grid>

              {/* Discount Configuration */}
              <Grid item xs={12}>
                <Divider sx={styles.divider} />
                <Typography sx={styles.sectionTitle}>Discount Configuration</Typography>
              </Grid>

              {/* Discount Type */}
              <Grid item xs={12} sm={4}>
                <Controller
                  control={control}
                  name="discountType"
                  render={({ field }) => (
                    <FormControl fullWidth size="small" error={!!errors.discountType} required>
                      <InputLabel>Discount Type</InputLabel>
                      <Select {...field} label="Discount Type">
                        <MenuItem value="percentage">Percentage (%)</MenuItem>
                        <MenuItem value="fixed">Fixed Amount</MenuItem>
                      </Select>
                      {errors.discountType && (
                        <FormHelperText>{errors.discountType.message}</FormHelperText>
                      )}
                      <FormHelperText>
                        Percentage: % off total | Fixed: specific amount off
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Currency */}
              <Grid item xs={12} sm={4}>
                <Controller
                  control={control}
                  name="currency"
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required>
                      <InputLabel>Currency</InputLabel>
                      <Select {...field} label="Currency">
                        {CURRENCIES.map((currency) => (
                          <MenuItem key={currency.code} value={currency.code}>
                            {currency.symbol} {currency.code} - {currency.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Currency for all amounts in this coupon
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Discount Percentage */}
              {discountType === 'percentage' && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="discountPercentage"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ''}
                        label="Discount Percentage"
                        fullWidth
                        size="small"
                        required
                        type="number"
                        inputProps={{ step: 0.01, min: 0, max: 100 }}
                        error={!!errors.discountPercentage}
                        helperText={errors.discountPercentage?.message || 'Enter value between 0-100'}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                        InputProps={{
                          endAdornment: (
                            <Tooltip title="The percentage discount to apply (e.g., 10 for 10% off)." arrow>
                              <InfoOutlinedIcon sx={styles.infoIcon} />
                            </Tooltip>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Discount Amount */}
              {discountType === 'fixed' && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="discountAmount.value"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ''}
                        label="Discount Amount"
                        fullWidth
                        size="small"
                        required
                        type="number"
                        inputProps={{ step: 0.01, min: 0 }}
                        error={!!errors.discountAmount?.value}
                        helperText={errors.discountAmount?.value?.message || 'The fixed amount to discount'}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                        InputProps={{
                          endAdornment: (
                            <Tooltip title="The fixed monetary amount to deduct from the order total." arrow>
                              <InfoOutlinedIcon sx={styles.infoIcon} />
                            </Tooltip>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Max Discount (for percentage) */}
              {discountType === 'percentage' && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="maxDiscount.value"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ''}
                        label="Max Discount Amount"
                        fullWidth
                        size="small"
                        type="number"
                        inputProps={{ step: 0.01, min: 0 }}
                        error={!!errors.maxDiscount?.value}
                        helperText={errors.maxDiscount?.value?.message || 'Optional cap on discount (leave blank for no limit)'}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                        InputProps={{
                          endAdornment: (
                            <Tooltip title="Maximum discount amount that can be applied (prevents excessive discounts on large orders)." arrow>
                              <InfoOutlinedIcon sx={styles.infoIcon} />
                            </Tooltip>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Min Purchase */}
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="minPurchase.value"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      label="Minimum Purchase Amount"
                      fullWidth
                      size="small"
                      type="number"
                      inputProps={{ step: 0.01, min: 0 }}
                      error={!!errors.minPurchase?.value}
                      helperText={errors.minPurchase?.value?.message || 'Optional minimum order value required'}
                      onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                      InputProps={{
                        endAdornment: (
                          <Tooltip title="Minimum order value required to use this coupon (leave blank for no minimum)." arrow>
                            <InfoOutlinedIcon sx={styles.infoIcon} />
                          </Tooltip>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Usage Limits */}
              <Grid item xs={12}>
                <Divider sx={styles.divider} />
                <Typography sx={styles.sectionTitle}>Usage Limits</Typography>
              </Grid>

              {/* Total Usage Limit */}
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="usageLimit"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      label="Total Usage Limit"
                      fullWidth
                      size="small"
                      type="number"
                      inputProps={{ min: 1 }}
                      error={!!errors.usageLimit}
                      helperText={errors.usageLimit?.message || 'Max times this coupon can be used (leave blank for unlimited)'}
                      onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                      InputProps={{
                        endAdornment: (
                          <Tooltip title="Maximum number of times this coupon can be redeemed across all customers." arrow>
                            <InfoOutlinedIcon sx={styles.infoIcon} />
                          </Tooltip>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Per User Usage Limit */}
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="userUsageLimit"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      label="Per User Usage Limit"
                      fullWidth
                      size="small"
                      type="number"
                      inputProps={{ min: 1 }}
                      error={!!errors.userUsageLimit}
                      helperText={errors.userUsageLimit?.message || 'Max times one user can use this (leave blank for unlimited)'}
                      onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                      InputProps={{
                        endAdornment: (
                          <Tooltip title="Maximum number of times a single customer can use this coupon." arrow>
                            <InfoOutlinedIcon sx={styles.infoIcon} />
                          </Tooltip>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Valid Period */}
              <Grid item xs={12}>
                <Divider sx={styles.divider} />
                <Typography sx={styles.sectionTitle}>Valid Period</Typography>
              </Grid>

              {/* Start Date */}
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="validFrom"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      label="Start Date"
                      fullWidth
                      size="small"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.validFrom}
                      helperText={errors.validFrom?.message || 'When this coupon becomes active'}
                      onChange={(e) => field.onChange(e.target.value || null)}
                      InputProps={{
                        endAdornment: (
                          <Tooltip title="Date when customers can start using this coupon." arrow>
                            <InfoOutlinedIcon sx={styles.infoIcon} />
                          </Tooltip>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* End Date */}
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="validTo"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      label="End Date"
                      fullWidth
                      size="small"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.validTo}
                      helperText={errors.validTo?.message || 'When this coupon expires'}
                      onChange={(e) => field.onChange(e.target.value || null)}
                      InputProps={{
                        endAdornment: (
                          <Tooltip title="Last date when this coupon can be used." arrow>
                            <InfoOutlinedIcon sx={styles.infoIcon} />
                          </Tooltip>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Applicable Order Types */}
              <Grid item xs={12}>
                <Divider sx={styles.divider} />
                <Typography sx={styles.sectionTitle}>Applicable Order Types</Typography>
                <Typography sx={styles.sectionHint}>
                  Select which order types this coupon can be applied to
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="applicableOrderType"
                  render={({ field }) => (
                    <Box>
                      <Box sx={styles.checkboxGroup}>
                        {Object.entries(ORDER_TYPE_LABELS).map(([key, label]) => {
                          const value = parseInt(key);
                          return (
                            <FormControlLabel
                              key={key}
                              control={
                                <Checkbox
                                  checked={field.value.includes(value)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      field.onChange([...field.value, value]);
                                    } else {
                                      field.onChange(field.value.filter((v) => v !== value));
                                    }
                                  }}
                                  size="small"
                                />
                              }
                              label={<Typography sx={styles.checkboxLabel}>{label}</Typography>}
                            />
                          );
                        })}
                      </Box>
                      {errors.applicableOrderType && (
                        <FormHelperText error sx={{ ml: 2 }}>
                          {errors.applicableOrderType.message as string}
                        </FormHelperText>
                      )}
                    </Box>
                  )}
                />
              </Grid>

              {/* Applicable Categories */}
              <Grid item xs={12}>
                <Divider sx={styles.divider} />
                <Box sx={styles.sectionHeader}>
                  <Box>
                    <Typography sx={styles.sectionTitle}>Applicable Categories (Optional)</Typography>
                    <Typography sx={styles.sectionHint}>
                      Select categories where this coupon can be used. Leave empty for all categories.
                    </Typography>
                  </Box>
                  <Tooltip title="Restrict this coupon to specific menu categories. If none selected, it applies to all categories." arrow>
                    <InfoOutlinedIcon sx={styles.infoIcon} />
                  </Tooltip>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="_idCategories"
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>Categories</InputLabel>
                      <Select
                        {...field}
                        multiple
                        label="Categories"
                        disabled={isLoadingCategories || isCategoriesError || categoriesData.length === 0}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 400,
                              width: 300,
                            },
                          },
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                        }}
                        renderValue={(selected) => {
                          const selectedCategories = categoriesData.filter((cat: TMenuCategory) => 
                            (selected as string[]).includes(cat._id)
                          );
                          return selectedCategories.map((cat: TMenuCategory) => cat.name).join(', ');
                        }}
                      >
                        {categoriesData.map((category: TMenuCategory) => (
                          <MenuItem key={category._id} value={category._id}>
                            <Checkbox checked={(field.value || []).includes(category._id)} size="small" />
                            <Typography sx={{ ml: 1 }}>{category.name}</Typography>
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText error={isCategoriesError}>
                        {isLoadingCategories
                          ? 'Loading categories...'
                          : isCategoriesError 
                          ? 'Failed to load categories'
                          : categoriesData.length === 0
                          ? 'No categories available. Please add menu categories first.'
                          : (field.value || []).length === 0 
                          ? 'No categories selected - coupon applies to all categories'
                          : `Selected ${(field.value || []).length} ${(field.value || []).length === 1 ? 'category' : 'categories'}`
                        }
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Availability Rules */}
              <Grid item xs={12}>
                <Divider sx={styles.divider} />
                <Box sx={styles.sectionHeader}>
                  <Box>
                    <Typography sx={styles.sectionTitle}>Availability Rules (Optional)</Typography>
                    <Typography sx={styles.sectionHint}>
                      Restrict when this coupon can be used based on day and time
                    </Typography>
                  </Box>
                  <Tooltip title="Set specific days and times when this coupon is valid. Useful for happy hour or weekend specials." arrow>
                    <InfoOutlinedIcon sx={styles.infoIcon} />
                  </Tooltip>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <AvailabilityRules control={control} />
              </Grid>

              {/* Tiered Discounts */}
              <Grid item xs={12}>
                <Divider sx={styles.divider} />
                <Box sx={styles.sectionHeader}>
                  <Box>
                    <Typography sx={styles.sectionTitle}>Tiered Discounts (Optional)</Typography>
                    <Typography sx={styles.sectionHint}>
                      Offer greater discounts for higher order values. When tiers are set, the root discount above is ignored.
                    </Typography>
                  </Box>
                  <Tooltip title="Create progressive discounts. Example: 10% off orders £20+, 15% off orders £50+." arrow>
                    <InfoOutlinedIcon sx={styles.infoIcon} />
                  </Tooltip>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    appendTier({
                      minOrderValue: { amount: 0, currency: DEFAULT_CURRENCY },
                      discount: { type: 'percentage', value: 0 },
                    })
                  }
                  sx={styles.addButton}
                >
                  Add Tier
                </Button>

                {tierFields.map((field, index) => (
                  <Box key={field.id} sx={styles.tierRow}>
                    <Box sx={styles.tierHeader}>
                      <Typography sx={styles.tierLabel}>Tier {index + 1}</Typography>
                      <IconButton size="small" onClick={() => removeTier(index)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={3}>
                        <Controller
                          control={control}
                          name={`tiers.${index}.minOrderValue.amount`}
                          render={({ field: f, fieldState: { error } }) => (
                            <TextField
                              {...f}
                              label="Min Order Value"
                              size="small"
                              type="number"
                              fullWidth
                              required
                              inputProps={{ step: 0.01, min: 0 }}
                              error={!!error}
                              helperText={error?.message}
                              onChange={(e) => f.onChange(parseFloat(e.target.value) || 0)}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} sm={2}>
                        <Controller
                          control={control}
                          name={`tiers.${index}.minOrderValue.currency`}
                          render={({ field: f }) => (
                            <TextField {...f} label="Currency" size="small" fullWidth />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <Controller
                          control={control}
                          name={`tiers.${index}.discount.type`}
                          render={({ field: f }) => (
                            <FormControl fullWidth size="small">
                              <InputLabel>Discount Type</InputLabel>
                              <Select {...f} label="Discount Type">
                                <MenuItem value="percentage">Percentage</MenuItem>
                                <MenuItem value="fixed">Fixed Amount</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Controller
                          control={control}
                          name={`tiers.${index}.discount.value`}
                          render={({ field: f, fieldState: { error } }) => (
                            <TextField
                              {...f}
                              label="Discount Value"
                              size="small"
                              type="number"
                              fullWidth
                              required
                              inputProps={{ step: 0.01, min: 0 }}
                              error={!!error}
                              helperText={error?.message}
                              onChange={(e) => f.onChange(parseFloat(e.target.value) || 0)}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={styles.actionButtons}>
                  <Button onClick={handleCancel} sx={styles.cancelButton} size="large">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isCreating || isUpdating}
                    startIcon={<SaveIcon />}
                    sx={styles.submitButton}
                    size="large"
                  >
                    {isCreating || isUpdating ? 'Saving...' : isEdit ? 'Update Coupon' : 'Create Coupon'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>

      <AlertMsg
        msgAlert={msgAlert}
        onCloseAlertMsg={() => setMsgAlert({ ...msgAlert, open: false })}
      />
    </Container>
  );
};

export default CouponFormPage;

const styles = {
  mainView: {
    backgroundColor: '#FFF',
    borderRadius: '8px',
    minHeight: 'calc(100vh - 200px)',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    minHeight: '300px',
  },
  errorTitle: {
    fontFamily: 'GilroySemiBold',
    fontSize: 18,
    color: '#d32f2f',
    marginBottom: '0.5rem',
  },
  errorMessage: {
    fontFamily: 'GilroyRegular',
    fontSize: 14,
    color: '#666',
    marginBottom: '1.5rem',
    textAlign: 'center' as const,
    maxWidth: '500px',
  },
  backButton: {
    backgroundColor: '#1a1a1a',
    fontFamily: 'GilroySemiBold',
    fontSize: 13,
    textTransform: 'none' as const,
    '&:hover': { backgroundColor: '#333' },
  },
  sectionTitle: {
    fontFamily: 'GilroySemiBold',
    fontSize: 16,
    color: '#1a1a1a',
    mb: 0.5,
  },
  sectionHint: {
    fontFamily: 'GilroyRegular',
    fontSize: 13,
    color: '#888',
    mb: 1,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 2,
  },
  fieldWrapper: {
    position: 'relative' as const,
  },
  infoIcon: {
    fontSize: 18,
    color: '#999',
    cursor: 'help',
    ml: 0.5,
  },
  divider: {
    my: 2,
  },
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
    gap: 2,
    ml: 1,
  },
  checkboxLabel: {
    fontFamily: 'GilroyRegular',
    fontSize: 14,
  },
  addButton: {
    fontFamily: 'GilroySemiBold',
    fontSize: 13,
    textTransform: 'none' as const,
    color: '#1a1a1a',
    border: '1px dashed #ccc',
    mb: 2,
  },
  tierRow: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    p: 2,
    mb: 2,
    backgroundColor: '#fafafa',
  },
  tierHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 2,
  },
  tierLabel: {
    fontFamily: 'GilroySemiBold',
    fontSize: 14,
    color: '#555',
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 2,
  },
  cancelButton: {
    fontFamily: 'GilroySemiBold',
    fontSize: 14,
    textTransform: 'none' as const,
    color: '#666',
    border: '1px solid #ddd',
  },
  submitButton: {
    backgroundColor: '#1a1a1a',
    fontFamily: 'GilroySemiBold',
    fontSize: 14,
    textTransform: 'none' as const,
    px: 4,
    '&:hover': {
      backgroundColor: '#333',
    },
  },
};
